import { useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { usePixiApp } from '../../services/pixiApp/pixiAppProvider';
import { useApi } from '../../services/api/ApiProvider';
import { deleteDeck as apiDeleteDeck } from '../../services/api/apiService';

import CollectionManager from '../../features/collection/managers/collectionManager';
import DecksManager from '../../features/decks/managers/decksManager';
import DeckEditorManager from '../../features/decks/managers/DeckEditorManager';
import LoadingSpinnerManager from '../../features/common/managers/LoadingSpinnerManager';
import ShopManager from '../../features/shop/managers/shopManager';
import './style.css';

const CollectionPage = () => {
    const { app, setAutoResize } = usePixiApp();
    const {
        collection,
        fetchCollection,
        fullCollection,
        fetchFullCollection,
        decks,
        fetchDecks,
        selectedDeck,
        setSelectedDeck,
        hasFreeShopItem,
    } = useApi();
    const navigate = useNavigate();
    const location = useLocation();

    const pixiContainerRef = useRef(null);
    const collectionManagerRef = useRef(null);
    const decksManagerRef = useRef(null);
    const loadingSpinnerManagerRef = useRef(null);
    const decksRef = useRef(decks);
    const selectedDeckRef = useRef(selectedDeck);
    const locationHashRef = useRef(location.hash);
    const hasFreeShopItemRef = useRef(hasFreeShopItem);
    decksRef.current = decks;
    selectedDeckRef.current = selectedDeck;
    locationHashRef.current = location.hash;
    hasFreeShopItemRef.current = hasFreeShopItem;

    const goBack = () => {
        navigate('/lobby');
    };

    const goToShop = async () => {
        loadingSpinnerManagerRef.current.attachToPixiApp(app);
        await ShopManager.loadStaticAssets();
        navigate('/shop');
    };

    const openDeckSelection = () => {
        navigate('/collection#decks');
    };

    const closeDeckSelection = () => {
        navigate(-1);
    };

    const createNewDeck = async () => {
        loadingSpinnerManagerRef.current.attachToPixiApp(app);
        await DeckEditorManager.loadAssets();
        navigate('/decks/create');
    };

    const editDeck = async (deckId) => {
        loadingSpinnerManagerRef.current.attachToPixiApp(app);
        await DeckEditorManager.loadAssets();
        navigate(`/decks/edit/${deckId}`);
    };

    const selectDeck = (deckId) => {
        setSelectedDeck(decksRef.current.find(deck => deck.id === deckId));
        navigate(-1);
    };

    const confirmDeleteDeck = async (deckId) => {
        await apiDeleteDeck(deckId);
        await fetchDecks();
    };

    useEffect(() => {
        let cancelled = false;

        const onAppResize = () => {
            const { width, height } = app.screen;
            collectionManagerRef.current?.resize(width, height);
            decksManagerRef.current?.resize(width, height);
        };

        const initPage = async () => {
            if (!app || !pixiContainerRef.current) return;

            pixiContainerRef.current.addEventListener('touchstart', (e) => e.preventDefault(), { passive: false });

            const collectionManager = new CollectionManager();
            let decksSnapshot = decksRef.current;
            let selectedDeckId = selectedDeckRef.current?.id;
            let decksManager = new DecksManager(decksSnapshot, selectedDeckId);
            const loadingSpinnerManager = new LoadingSpinnerManager(app.screen.width, app.screen.height);

            await Promise.all([
                CollectionManager.loadAssets(),
                decksManager.loadAssets(),
            ]);
            if (cancelled) return;

            if (decksRef.current !== decksSnapshot || selectedDeckRef.current?.id !== selectedDeckId) {
                decksSnapshot = decksRef.current;
                selectedDeckId = selectedDeckRef.current?.id;
                decksManager = new DecksManager(decksSnapshot, selectedDeckId);
                await decksManager.loadAssets();
                if (cancelled) return;
            }

            collectionManager.attachToPixiApp(app);
            pixiContainerRef.current.appendChild(app.canvas);
            collectionManager.setShopBadgeVisible(hasFreeShopItemRef.current);
            collectionManager.buildMainLayout();
            collectionManager.onBackButtonClick = goBack;
            collectionManager.onGoToShopButtonClick = goToShop;
            collectionManager.onMyDecksButtonClick = openDeckSelection;
            collectionManagerRef.current = collectionManager;

            decksManager.buildMainLayout(app.screen.width, app.screen.height);
            decksManager.buildDeckList();
            decksManager.onBackButtonClick = closeDeckSelection;
            decksManager.onNewDeckButtonClick = createNewDeck;
            decksManager.onEditDeckButtonClick = editDeck;
            decksManager.onSelectDeckButtonClick = selectDeck;
            decksManager.onConfirmDeleteDeck = confirmDeleteDeck;
            decksManagerRef.current = decksManager;
            loadingSpinnerManagerRef.current = loadingSpinnerManager;
            onAppResize();

            if (locationHashRef.current === '#decks') {
                decksManager.attachToPixiApp(app);
                collectionManager.setSearchBarShown(false);
            }

            if (collection) {
                collectionManager.setCollection(collection);
            } else {
                fetchCollection();
            }

            if (fullCollection) {
                collectionManager.setFullCollection(fullCollection);
            } else {
                fetchFullCollection();
            }
        };

        window.addEventListener('appResize', onAppResize);
        setAutoResize(true);
        initPage();

        return () => {
            cancelled = true;
            window.removeEventListener('appResize', onAppResize);
            setAutoResize(false);
            if (collectionManagerRef.current && app) {
                collectionManagerRef.current.detachFromPixiApp(app);
            }

            if (decksManagerRef.current?.container?.parent && app) {
                decksManagerRef.current.detachFromPixiApp(app);
            }

            if (loadingSpinnerManagerRef.current?.container?.parent && app) {
                loadingSpinnerManagerRef.current.detachFromPixiApp(app);
            }
        };
    }, [app]);

    useEffect(() => {
        if (collectionManagerRef.current && collection) {
            collectionManagerRef.current.setCollection(collection);
        }
    }, [collection]);

    useEffect(() => {
        if (collectionManagerRef.current && fullCollection) {
            collectionManagerRef.current.setFullCollection(fullCollection);
        }
    }, [fullCollection]);

    useEffect(() => {
        collectionManagerRef.current?.setShopBadgeVisible(hasFreeShopItem);
    }, [hasFreeShopItem]);

    useEffect(() => {
        const manager = decksManagerRef.current;
        if (!manager || !decks) return;

        if (!manager.selectedTab) {
            const selectedId = selectedDeckRef.current?.id ?? manager.highlightedDeckForSelection;
            const deck = selectedId ? decks.find((item) => item.id === selectedId) : null;
            if (deck) {
                manager.highlightedDeckForSelection = deck.id;
                manager.selectedTab = deck.isPrebuilt ? 'prebuiltDecks' : 'myDecks';
            } else {
                manager.selectedTab = 'myDecks';
            }
            manager.buildTabsButtons(true);
        }

        manager.setDecks(decks);
    }, [decks]);

    useEffect(() => {
        if (!decksManagerRef.current) return;

        if (location.hash === '#decks') {
            decksManagerRef.current.attachToPixiApp(app);
            collectionManagerRef.current?.setSearchBarShown(false);
        } else if (location.hash === '') {
            if (decksManagerRef.current.container.parent) {
                decksManagerRef.current.detachFromPixiApp(app);
            }
            collectionManagerRef.current?.setSearchBarShown(true);
        }
    }, [location, app]);

    return (
        <div style={{ width: '100dvw', height: '100dvh', overflow: 'hidden' }} ref={pixiContainerRef} />
    );
};

export default CollectionPage;
